import Phaser from 'phaser';
import type { MapData, MapLayer, TileData } from '@shared/types/map';
import type { AssetsIndex } from '@shared/types/surface';
import { TILE_SIZE, WORLD_TILES } from '@shared/constants';
import { isDecorationKey, getDecorationAssetUrl } from '../util/decorationAssets';
import {
  preloadSurfaceAtlases,
  setupSurfaceTextures,
  getSurfaceTextureKey,
  getSurfaceById,
} from '../sprites/SurfaceSpriteManager';
import {
  preloadWaterFrames,
  getWaterTypeForSprite,
  getWaterFrameTextureKey,
  getWaterFrameCount,
  WATER_FRAME_MS,
} from '../sprites/WaterSpriteManager';
import { WaterPipeline, WATER_PIPELINE_KEY } from '../sprites/WaterPipeline';
import { LightingSystem, type LightConfig } from '../systems/LightingSystem';
import { TimeOfDayManager } from '../systems/TimeOfDayManager';
import { AmbientCrowd } from './AmbientCrowd';

/**
 * Metadatos de decoración para ajustes especiales de renderizado.
 * `light` (igual que en GameScene): si la decoración emite luz (antorcha/fogata),
 * se registra como fuente de luz en el LightingSystem del fondo.
 */
interface DecorationMeta {
  name: string;
  type: string;
  light?: LightConfig;
}

/**
 * Configuración para el MapBackgroundRenderer.
 */
export interface MapBackgroundConfig {
  /** MapData a renderizar */
  mapData: MapData;
  /** Coordenada X del tile central (en tiles del mapa) */
  centerTileX: number;
  /** Coordenada Y del tile central (en tiles del mapa) */
  centerTileY: number;
  /** Índice de decoraciones para ajustes especiales (opcional) */
  decorationIndex?: Record<string, DecorationMeta>;
  /** Callback cuando la escena está lista */
  onReady?: () => void;
  /** Progreso de carga 0..1 (preload pesa 0..0.7, atlas de superficie 0.7..1). */
  onProgress?: (p: number) => void;
  /** "Ciudad viva": NPCs parados + gente deambulando (simulación local, sin server).
   *  Default true. Ver AmbientCrowd. */
  ambientCrowd?: boolean;
}

/**
 * Escena de Phaser que renderiza un mapa como fondo estático.
 * No incluye NPCs ni entidades, solo las capas gráficas del mapa.
 */
class MapBackgroundScene extends Phaser.Scene {
  private config: MapBackgroundConfig;
  private assetsLoaded = false;
  /** Multitud ambiente (NPCs parados + transeúntes). Null si está desactivada. */
  private crowd: AmbientCrowd | null = null;
  /** Overlay negro semi-transparente para atenuar el fondo (resalta menos que la UI). */
  private darkOverlay: Phaser.GameObjects.Rectangle | null = null;

  /** Sprites de agua para animarlos en update() (igual que GameScene). */
  private waterSprites: Array<{ sprite: Phaser.GameObjects.Image; type: string }> = [];
  /** Frame global de agua (todos los bloques muestran el mismo, océano coherente). */
  private waterFrame = 0;
  /** Acumulador de tiempo para avanzar el frame cada WATER_FRAME_MS. */
  private waterAccumMs = 0;
  /**
   * Pipeline de agua (mismo shader "Seascape" que GameScene): dibuja el brillo /
   * oleaje procedural sobre cada bloque de agua. Se aplica con setPipeline y sus
   * uniforms se actualizan por frame en update(). Null si no hay renderer WebGL.
   */
  private waterPipeline: WaterPipeline | null = null;
  /**
   * Set de todos los tiles de agua ("x,y"): lo usa waterCalmFactor para atenuar el
   * oleaje en la costa (igual que GameScene.waterTileSet).
   */
  private waterTileSet = new Set<string>();
  /**
   * Set "bx,by" (origen de bloque) de bloques ORILLA (tierra/costa encima). El bajío/
   * cyan se mide contra este set y no contra "no-agua", porque los bloques de costa
   * tienen agua de relleno en la capa 1 (igual que GameScene.shoreBlockSet).
   */
  private shoreBlockSet = new Set<string>();

  /**
   * Iluminación del fondo (paridad con GameScene): el TimeOfDayManager calcula la
   * oscuridad ambiente según la hora local y el LightingSystem la dibuja como overlay,
   * perforando las antorchas/fogatas del mapa. Así el login/selección/creación se ven
   * de día o de noche igual que el juego.
   */
  private lightingSystem: LightingSystem | null = null;
  private timeOfDay: TimeOfDayManager | null = null;

  constructor(config: MapBackgroundConfig) {
    super({ key: 'MapBackgroundScene' });
    this.config = config;
  }

  preload(): void {
    // Progreso del preload: pesa 0..0.7 del total (el resto son los atlas de
    // superficie que se cargan en create()).
    this.load.on('progress', (v: number) => this.config.onProgress?.(v * 0.7));

    // Cargar índice de assets para metadatos de decoraciones
    this.load.json('assets-index', '/assets/assets-index.json');

    // Cargar textura de patrón para el fondo
    this.load.image('bg-pattern', '/assets/world/floors/pattern1.png');

    // Precargar decoraciones del mapa (árboles / legacy)
    this.preloadMapAssets(this.config.mapData);

    // Frames de agua (URLs estáticas, no dependen de assets-index).
    // Los atlas de superficie (pisos) se cargan en create() porque dependen de
    // que assets-index.json ya esté en cache.
    preloadWaterFrames(this);

    // "Ciudad viva": construir la multitud ambiente y precargar SUS sprites (bodies/heads
    // acotados) en esta misma pasada. Simulación local, sin conexión al server.
    if (this.config.ambientCrowd !== false) {
      this.crowd = new AmbientCrowd(this, this.config.mapData, this.config.centerTileX, this.config.centerTileY, {
        maxPeople: 5, // cantidad real = random 2..5 por carga (ver AmbientCrowd.create)
        radius: 12,
      });
      this.crowd.preloadSprites();
    }
  }

  create(): void {
    const rawAssetsIndex = this.cache.json.get('assets-index') as
      | (AssetsIndex & { decorations?: Record<string, DecorationMeta> })
      | undefined;

    // Obtener índice de decoraciones si no se proporcionó
    if (!this.config.decorationIndex && rawAssetsIndex?.decorations) {
      this.config.decorationIndex = rawAssetsIndex.decorations;
    }

    // Tras setup de superficies: renderizar y avisar que el fondo está listo.
    const finishSetup = (): void => {
      // Extraer las texturas individuales de superficie (pisos) desde los atlas.
      setupSurfaceTextures(this);

      // Pipeline de agua (mismo shader que el juego). Persiste en el renderer entre
      // recreaciones de la escena, por eso el guard has/get/add.
      const renderer = this.game.renderer;
      if (renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
        this.waterPipeline = renderer.pipelines.has(WATER_PIPELINE_KEY)
          ? (renderer.pipelines.get(WATER_PIPELINE_KEY) as WaterPipeline)
          : (renderer.pipelines.add(
              WATER_PIPELINE_KEY,
              new WaterPipeline(this.game)
            ) as WaterPipeline);
      }

      // Sistema de iluminación (paridad con GameScene): oscuridad por horario + luces
      // del mapa. Se crea ANTES de renderMap para poder registrar las luces de
      // decoración (antorchas) a medida que se instancian sus sprites.
      this.timeOfDay = new TimeOfDayManager();
      this.timeOfDay.update();
      this.lightingSystem = new LightingSystem(this);

      // Renderizar el mapa (registra de paso las luces de decoración)
      this.renderMap();

      // Luces estáticas definidas en el mapa (torches/campfires colocadas por posición)
      this.registerMapLights();

      // Oscuridad inicial según la hora local (evita un flash claro en el primer frame)
      this.lightingSystem.setAmbientDarkness(this.timeOfDay.getAmbientDarkness());
      this.lightingSystem.update(16);

      // Configurar la cámara centrada en el tile especificado
      this.setupCamera();

      // Escuchar cambios de tamaño para recentrar la cámara
      this.scale.on('resize', this.handleResize, this);

      // Multitud ambiente: crear los sprites (NPCs parados + transeúntes) tras el mapa.
      this.crowd?.create();

      // Overlay negro que atenúa TODO el fondo (mapa + crowd), por encima de todo lo de
      // la escena y fijo a pantalla (scrollFactor 0). Hace que la UI del login resalte más.
      const cam = this.cameras.main;
      this.darkOverlay = this.add
        .rectangle(0, 0, cam.width, cam.height, 0x000000, 0.35)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(900);

      // Limpiar el LightingSystem (su textura de overlay) al destruir la escena.
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.lightingSystem?.destroy();
        this.lightingSystem = null;
        this.crowd?.destroy();
        this.crowd = null;
      });

      this.assetsLoaded = true;
      this.config.onProgress?.(1);
      this.config.onReady?.();
    };

    // Segunda pasada de carga: los atlas de superficie (pisos) viven en
    // assets-index.json, que recién está disponible en create(). Mismo patrón que
    // GameScene: encolar atlas, esperar 'complete' y recién ahí hornear texturas.
    const hasSurfaces = !!(
      rawAssetsIndex?.surfaces && Object.keys(rawAssetsIndex.surfaces).length > 0
    );
    if (hasSurfaces) {
      preloadSurfaceAtlases(this, rawAssetsIndex as AssetsIndex);
      // Quitar el listener del preload y mapear esta segunda pasada a 0.7..1.
      this.load.off('progress');
      this.load.on('progress', (v: number) => this.config.onProgress?.(0.7 + v * 0.3));
      this.load.once('complete', finishSetup);
      this.load.start();
    } else {
      finishSetup();
    }
  }

  /**
   * Maneja el evento de resize para recentrar la cámara.
   */
  private handleResize(gameSize: Phaser.Structs.Size): void {
    this.cameras.main.setSize(gameSize.width, gameSize.height);
    this.centerCamera();
    this.darkOverlay?.setSize(gameSize.width, gameSize.height);
  }

  /**
   * Precarga los assets necesarios para las capas del mapa.
   */
  private preloadMapAssets(mapData: MapData): void {
    const seen = new Set<string>();

    for (const layer of mapData.layers) {
      // Saltar capas internas (no necesitan assets)
      if (layer.name.startsWith('_')) continue;

      for (const tile of layer.tiles) {
        const key = tile.sprite;
        if (!key || seen.has(key)) continue;
        seen.add(key);

        // Decoraciones: tree1..N (árboles) y decoration1..N (legacy)
        if (isDecorationKey(key)) {
          this.load.image(key, getDecorationAssetUrl(key));
        }
      }
    }
  }

  /**
   * Renderiza todas las capas del mapa.
   */
  private renderMap(): void {
    const { mapData, decorationIndex, centerTileX, centerTileY } = this.config;

    // Pre-pass: índice de TODOS los tiles de agua ("x,y") + bloques de ORILLA (tierra/
    // costa encima), necesario para calcular el factor de calma/bajío de cada bloque
    // antes de crear los sprites (un bloque mira a vecinos que aún no se procesaron).
    this.waterTileSet.clear();
    this.shoreBlockSet.clear();
    for (const layer of mapData.layers) {
      if (layer.name.startsWith('_')) continue;
      for (const tile of layer.tiles) {
        if (this.isWaterTile(tile.sprite, layer.name)) {
          this.waterTileSet.add(`${tile.x},${tile.y}`);
        } else if (MapBackgroundScene.isShoreSurfaceLayer(layer.name)) {
          const bx = Math.floor(tile.x / 4) * 4;
          const by = Math.floor(tile.y / 4) * 4;
          this.shoreBlockSet.add(`${bx},${by}`);
        }
      }
    }

    // Crear fondo con patrón que cubra toda el área visible y más
    // El TileSprite se posiciona en el centro del mapa y es lo suficientemente grande
    const bgSize = Math.max(window.innerWidth, window.innerHeight) * 3;
    const centerWorldX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const centerWorldY = centerTileY * TILE_SIZE + TILE_SIZE / 2;

    if (this.textures.exists('bg-pattern')) {
      // pattern1.png ahora es el atlas 9-slice del pasto (768×384, con mordidas
      // transparentes). Tilear el atlas entero muestra la grilla (se ve cuadriculado),
      // así que recortamos el bloque CENTRAL sólido (4×4 tiles = 128px, en (128,128))
      // y tileamos solo eso → pasto seamless.
      const tex = this.textures.get('bg-pattern');
      const frameKey = 'grass-center';
      if (!tex.has(frameKey)) {
        tex.add(frameKey, 0, 128, 128, 128, 128);
      }
      const background = this.add.tileSprite(
        centerWorldX,
        centerWorldY,
        bgSize,
        bgSize,
        'bg-pattern',
        frameKey
      );
      background.setOrigin(0.5, 0.5);
      // Detrás de TODO (incluso del suelo, que va en -200): el patrón solo rellena
      // los huecos fuera del mapa, no tapa las superficies reales.
      background.setDepth(-1000);
      background.setAlpha(0.6); // Semitransparente para efecto atmosférico
    }

    for (const layer of mapData.layers) {
      // Saltar capas internas (no se renderizan)
      if (layer.name.startsWith('_')) continue;

      for (const tile of layer.tiles) {
        // Agua animada por BLOQUE 4×4 (128px): un solo sprite en el tile origen
        // del bloque, salteando los otros 15. Mismo criterio que
        // GameScene.createMapTileObject. Se guarda en waterSprites para animarlo
        // en update() ciclando los frames.
        const waterType = this.isWaterTile(tile.sprite, layer.name)
          ? getWaterTypeForSprite(tile.sprite)
          : null;
        if (waterType) {
          if (tile.x % 4 !== 0 || tile.y % 4 !== 0) continue;
          const blockKey = getWaterFrameTextureKey(waterType, 0);
          if (!this.textures.exists(blockKey)) continue;
          const blockX = tile.x * TILE_SIZE + (4 * TILE_SIZE) / 2;
          const blockY = tile.y * TILE_SIZE + (4 * TILE_SIZE) / 2;
          const waterSprite = this.add.image(blockX, blockY, blockKey);
          waterSprite.setOrigin(0.5, 0.5);
          waterSprite.setDepth(this.getDepthForMapLayer(layer.name, tile.y, tile.zOverride));
          if (this.waterPipeline) {
            waterSprite.setPipeline(WATER_PIPELINE_KEY);
            // Atenuar el oleaje en la costa: el factor "calma" (0..1) viaja al shader
            // como tint gris (lee outTint.r). Se calcula POR ESQUINA (no por bloque) y
            // se setea como tint de 4 vértices: la GPU lo interpola suave a través del
            // quad y, como bloques vecinos comparten esquina con el mismo valor, los
            // bordes coinciden → sin costuras cuadradas hacia la costa.
            waterSprite.setTint(
              MapBackgroundScene.grayTint(this.cornerCalm(tile.x, tile.y)),
              MapBackgroundScene.grayTint(this.cornerCalm(tile.x + 4, tile.y)),
              MapBackgroundScene.grayTint(this.cornerCalm(tile.x, tile.y + 4)),
              MapBackgroundScene.grayTint(this.cornerCalm(tile.x + 4, tile.y + 4))
            );
          }
          this.waterSprites.push({ sprite: waterSprite, type: waterType });
          continue;
        }

        // Una superficie multi-tile (4×4 = 128px: pasto/arena/nieve/COSTA) se centra en el
        // CENTRO DEL BLOQUE (igual que el agua y que GameScene.createMapTileObject), usando
        // tilesX/tilesY de la surface. Si se centrara en el primer tile (TILE_SIZE/2), quedaría
        // 48px arriba-izquierda respecto del agua y la costa pasto↔agua saldría DURA en los
        // lados S/E — que es como se veía mal el fondo. 1×1 (caminos) y deco → tilesX/Y=1 → tile+16.
        const surfaceDef = getSurfaceById(tile.sprite);
        const tilesX = surfaceDef?.tilesX ?? 1;
        const tilesY = surfaceDef?.tilesY ?? 1;

        // Para el suelo y deco baja, centramos en el bloque.
        // Para decoraciones superiores / techos, anclamos a la base del tile.
        const isGroundLike = layer.name === 'ground' || layer.name === 'ground_deco_under'
          || layer.name === 'layer1_ground' || layer.name === 'layer2_floor_objects';
        const worldX = tile.x * TILE_SIZE + (tilesX * TILE_SIZE) / 2;
        const worldY = isGroundLike
          ? tile.y * TILE_SIZE + (tilesY * TILE_SIZE) / 2
          : tile.y * TILE_SIZE + TILE_SIZE;

        // Resolver textura: las superficies (pisos) usan una key generada por el
        // SurfaceSpriteManager; las decoraciones usan su key directa.
        let textureKey = tile.sprite;
        const surfaceTextureKey = getSurfaceTextureKey(tile.sprite);
        if (this.textures.exists(surfaceTextureKey)) {
          textureKey = surfaceTextureKey;
        }
        if (!this.textures.exists(textureKey)) {
          continue;
        }

        const sprite = this.add.image(worldX, worldY, textureKey);
        // Suelo centrado; decoraciones y techos pegados a la base del tile.
        sprite.setOrigin(0.5, isGroundLike ? 0.5 : 1);

        // Luz de decoración (antorchas/fogatas): registrarla en el LightingSystem
        // para que perfore la oscuridad, igual que GameScene.createMapTileObject.
        const decoMeta = this.config.decorationIndex?.[tile.sprite];
        if (decoMeta?.light && this.lightingSystem) {
          const lightConfig = decoMeta.light;
          const colorValue =
            typeof lightConfig.color === 'string'
              ? parseInt(lightConfig.color.replace('0x', ''), 16)
              : lightConfig.color;
          this.lightingSystem.addLightSource(`deco_${tile.x}_${tile.y}_${tile.sprite}`, {
            x: worldX,
            y: worldY,
            radius: lightConfig.radius,
            intensity: lightConfig.intensity,
            color: colorValue,
            flicker: lightConfig.flicker ?? false,
          });
        }

        const depth = this.getDepthForMapLayer(layer.name, tile.y, tile.zOverride);
        sprite.setDepth(depth);
      }
    }
  }

  /**
   * Registra las luces estáticas definidas en el mapa (torches/campfires colocadas
   * por posición de tile). Espejo de GameScene.registerMapLights: soporta fuentes de
   * OSCURIDAD (`dark:true`, sombra suave) y de luz.
   */
  private registerMapLights(): void {
    const lights = this.config.mapData.lights;
    if (!lights || !this.lightingSystem) return;

    for (const light of lights) {
      const worldX = light.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = light.y * TILE_SIZE + TILE_SIZE / 2;
      const colorValue =
        typeof light.color === 'string'
          ? parseInt(light.color.replace('0x', ''), 16)
          : light.color;
      const lightId = `map_${light.id}`;

      if (light.dark) {
        this.lightingSystem.addDarkSource(lightId, {
          x: worldX,
          y: worldY,
          radius: light.radius,
          intensity: light.intensity,
          color: colorValue,
        });
        continue;
      }

      this.lightingSystem.addLightSource(lightId, {
        x: worldX,
        y: worldY,
        radius: light.radius,
        intensity: light.intensity,
        color: colorValue,
        flicker: light.flicker ?? false,
      });
    }
  }

  /**
   * Detecta si un tile es de agua. Mismo criterio que GameScene.isWaterTile:
   * el agua vive en la capa de suelo (`layer1_ground`) y se identifica por el
   * sprite id o por el tipo de la surface.
   */
  private isWaterTile(spriteId: string, layerName: string): boolean {
    if (layerName !== 'layer1_ground') return false;
    if (spriteId.toLowerCase().includes('water')) return true;
    const surface = getSurfaceById(spriteId);
    return !!surface && surface.type === 'water';
  }

  /** Gris de 24 bits (R=G=B) que codifica la calma 0..1 en el canal R del tint. */
  private static grayTint(calm: number): number {
    const g = Math.round(Phaser.Math.Clamp(calm, 0, 1) * 255);
    return (g << 16) | (g << 8) | g;
  }

  /** Calma de un bloque cualquiera; 0 si no es agua (costa/tierra → oleaje plano). */
  private blockCalm(bx: number, by: number): number {
    if (!this.waterTileSet.has(`${bx},${by}`)) return 0;
    return this.waterCalmFactor(bx, by);
  }

  /**
   * Calma en la esquina superior-izquierda del bloque cuyo tile origen es (bx,by):
   * promedio de los 4 bloques que comparten esa esquina de la grilla. Bloques
   * adyacentes evalúan la MISMA esquina con los MISMOS 4 vecinos, así que el valor
   * coincide en los bordes compartidos y el tint por-vértice interpola sin costuras.
   */
  private cornerCalm(bx: number, by: number): number {
    return (
      0.25 *
      (this.blockCalm(bx - 4, by - 4) +
        this.blockCalm(bx, by - 4) +
        this.blockCalm(bx - 4, by) +
        this.blockCalm(bx, by))
    );
  }

  /** Capas de suelo cuyas superficies (pasto/arena, centro o borde) marcan ORILLA. */
  private static isShoreSurfaceLayer(name: string): boolean {
    return (
      name === 'ground' ||
      name === 'layer1_ground' ||
      name === 'ground_deco_under' ||
      name === 'layer2_floor_objects'
    );
  }

  /**
   * Factor de "calma" (0..1) de un bloque de agua: 0 en la ORILLA (agua baja → cyan) y
   * 1 en mar abierto. Igual que GameScene.waterCalmFactor: se mide contra `shoreBlockSet`
   * (bloques con tierra/costa encima) y NO contra "no-agua", porque los bloques de costa
   * tienen agua de relleno en la capa 1 (para el difuminado del pasto); si midiéramos
   * contra agua, el bajío caería un bloque tierra adentro (raro sobre el pasto y ausente
   * en el agua de la orilla).
   */
  private waterCalmFactor(tx: number, ty: number): number {
    // Igual que GameScene: bajío pleno (cyan) pegado a la orilla y DEGRADANDO hacia el mar.
    if (this.shoreBlockSet.has(`${tx},${ty}`)) return 0.06;
    const ANCHO_ANILLOS = 0;
    for (let r = 1; r <= ANCHO_ANILLOS; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // solo el borde del anillo
          if (this.shoreBlockSet.has(`${tx + dx * 4},${ty + dy * 4}`)) {
            return Math.max(0.06, r / (ANCHO_ANILLOS + 1));
          }
        }
      }
    }
    return 1.0;
  }

  /**
   * Anima el agua: avanza un frame global cada WATER_FRAME_MS y reasigna la
   * textura de cada bloque, así todos muestran el mismo frame (océano coherente).
   * Además actualiza los uniforms del pipeline de agua (tiempo + worldView) para
   * anclar el oleaje al mundo, igual que GameScene.update().
   */
  update(_time: number, delta: number): void {
    // Iluminación por horario (paridad con GameScene.update): recalcular la oscuridad
    // ambiente según la hora local y redibujar el overlay + antorchas. Va ANTES del
    // early-return del agua para que funcione aunque el encuadre no tenga agua.
    if (this.lightingSystem && this.timeOfDay) {
      this.timeOfDay.update();
      this.lightingSystem.setAmbientDarkness(this.timeOfDay.getAmbientDarkness());
      this.lightingSystem.update(delta);
    }

    // Multitud ambiente (antes del early-return del agua, para que ande sin agua en cuadro).
    this.crowd?.update(delta);

    if (this.waterSprites.length === 0) return;

    // Uniforms del shader de agua por frame (tiempo en segundos + worldView de la
    // cámara), lo que da el brillo/oleaje procedural anclado al mundo.
    if (this.waterPipeline) {
      this.waterPipeline.setFrame(this.time.now / 1000, this.cameras.main.worldView);
    }

    this.waterAccumMs += delta;
    if (this.waterAccumMs < WATER_FRAME_MS) return;
    this.waterAccumMs = 0;
    this.waterFrame++;
    for (const { sprite, type } of this.waterSprites) {
      const key = getWaterFrameTextureKey(type, this.waterFrame % getWaterFrameCount(type));
      if (this.textures.exists(key)) sprite.setTexture(key);
    }
  }

  /**
   * Configura la cámara para centrarla en el tile especificado.
   */
  private setupCamera(): void {
    this.centerCamera();
  }

  /**
   * Centra la cámara en el tile configurado.
   */
  private centerCamera(): void {
    const { centerTileX, centerTileY } = this.config;

    // Calcular posición central en píxeles
    const centerWorldX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const centerWorldY = centerTileY * TILE_SIZE + TILE_SIZE / 2;

    // Centrar la cámara en esa posición
    this.cameras.main.centerOn(centerWorldX, centerWorldY);
  }

  /**
   * Calcula la profundidad para un tile según su capa y posición Y.
   */
  private getDepthForMapLayer(
    layerName: string,
    tileY: number,
    zOverride?: number
  ): number {
    if (typeof zOverride === 'number') {
      return this.getDepthFromTileY(tileY) + zOverride;
    }

    // Sistema de 4 capas (sincronizado con GameScene.ts)
    // Capas 1, 2 y 4 usan DEPTH FIJO para sprites multi-tile
    switch (layerName) {
      // CAPA 1: Suelo base - DEPTH FIJO
      case 'ground':
      case 'layer1_ground':
        return -200;
      // CAPA 2: Decoración de piso - DEPTH FIJO
      case 'ground_deco_under':
      case 'layer2_floor_objects':
        return -100;
      // CAPA 3: Decoración con interacción Y - DEPTH DINÁMICO
      case 'ground_deco_over':
      case 'layer3_solid':
        return this.getDepthFromTileY(tileY) + 0.4;
      // CAPA 4: Techos - DEPTH FIJO. 798 (no 800): igual que GameScene, los techos van
      // JUSTO por debajo del overlay de iluminación (LightingSystem.OVERLAY_DEPTH = 799)
      // para que la oscuridad/luz ambiental los afecte como al resto del mundo.
      case 'roof':
      case 'layer4_roof':
        return 798;
      default:
        return this.getDepthFromTileY(tileY);
    }
  }

  /**
   * Normaliza la coordenada Y de grilla a un rango de depth.
   */
  private getDepthFromTileY(tileY: number): number {
    const clamped = Phaser.Math.Clamp(tileY, 0, WORLD_TILES - 1);
    const normalized = clamped / WORLD_TILES;
    return normalized * 700;
  }

  /**
   * Actualiza el centro de la cámara (para efectos de parallax o movimiento).
   */
  updateCenter(tileX: number, tileY: number): void {
    this.config.centerTileX = tileX;
    this.config.centerTileY = tileY;
    this.centerCamera();
  }
}

/**
 * Clase que maneja el renderizado de un mapa como fondo.
 * Encapsula la creación de la instancia de Phaser y la escena.
 * El fondo ocupa toda la ventana y se redimensiona automáticamente.
 */
export class MapBackgroundRenderer {
  private game: Phaser.Game | null = null;
  private scene: MapBackgroundScene | null = null;
  private container: HTMLDivElement;
  private config: MapBackgroundConfig;

  constructor(container: HTMLDivElement, config: MapBackgroundConfig) {
    this.container = container;
    this.config = config;
  }

  /**
   * Inicia el renderizado del mapa.
   * El canvas ocupará todo el tamaño de la ventana.
   */
  start(): void {
    // Limpiar cualquier contenido previo del contenedor
    this.container.innerHTML = '';

    this.scene = new MapBackgroundScene(this.config);

    const gameConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.WEBGL,
      parent: this.container,
      // Usar el tamaño completo de la ventana
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: '#000000',
      scene: this.scene,
      render: {
        pixelArt: true,
        antialias: false,
        roundPixels: true,
      },
      scale: {
        // RESIZE permite que el canvas se redimensione con la ventana
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        // Ocupar todo el contenedor padre
        width: '100%',
        height: '100%',
      },
      audio: {
        noAudio: true, // No necesitamos audio para el fondo
      },
    };

    this.game = new Phaser.Game(gameConfig);

    // Asegurar que el canvas ocupe todo el espacio
    // Phaser crea el canvas asíncronamente, así que esperamos un frame
    requestAnimationFrame(() => {
      const canvas = this.container.querySelector('canvas');
      if (canvas) {
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
      }
    });
  }

  /**
   * Actualiza el centro del mapa (para efectos de movimiento lento).
   */
  updateCenter(tileX: number, tileY: number): void {
    this.scene?.updateCenter(tileX, tileY);
  }

  /**
   * Destruye la instancia de Phaser y limpia recursos.
   */
  destroy(): void {
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
      this.scene = null;
    }
  }

  /**
   * Indica si el renderer está activo.
   */
  get isActive(): boolean {
    return this.game !== null;
  }
}
